import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format, subDays, parseISO, isValid } from 'date-fns';
import { FiFilter, FiDownload, FiRefreshCw, FiTrendingUp, FiTrendingDown, FiActivity, FiCheck, FiX, FiAlertCircle } from 'react-icons/fi';
import { metricsApi } from '../utils/api';

const COLORS = {
  primary: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#6366F1',
  gray: '#6B7280'
};

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#8B5CF6'];

function MetricsDashboard({ visitorId, monitoredProducts = [] }) {
  // State for data
  const [analytics, setAnalytics] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // State for filters
  const [filters, setFilters] = useState({
    dateRange: 7, // days
    productId: '', // specific product or empty for all
    changesOnly: false,
    successOnly: false,
    startDate: '',
    endDate: ''
  });

  // State for UI
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Computed date range
  const dateRange = useMemo(() => {
    if (filters.startDate && filters.endDate) {
      return {
        startDate: filters.startDate,
        endDate: filters.endDate
      };
    }
    
    const endDate = new Date();
    const startDate = subDays(endDate, filters.dateRange);
    
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  }, [filters.dateRange, filters.startDate, filters.endDate]);

  // Load analytics data
  const loadAnalytics = async () => {
    try {
      const params = {
        userId: visitorId,
        ...dateRange
      };
      
      if (filters.productId) {
        params.productId = filters.productId;
      }

      const response = await metricsApi.getAnalytics(params);
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
      if (err.response?.status !== 503) {
        setError('Failed to load analytics data');
      }
    }
  };

  // Load metrics data
  const loadMetrics = async () => {
    try {
      const params = {
        limit: 200,
        ...dateRange
      };

      if (filters.changesOnly) {
        params.changesOnly = true;
      }

      let response;
      if (filters.productId) {
        response = await metricsApi.getProductMetrics(filters.productId, params);
      } else {
        response = await metricsApi.getUserMetrics(visitorId, params);
      }

      if (response.data.success) {
        let metricsData = response.data.data;
        
        // Filter by success if needed
        if (filters.successOnly) {
          metricsData = metricsData.filter(m => m.checkStatus.success);
        }

        setMetrics(metricsData);
      }
    } catch (err) {
      console.error('Failed to load metrics:', err);
      if (err.response?.status !== 503) {
        setError('Failed to load metrics data');
      }
    }
  };

  // Load all data
  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      await Promise.all([loadAnalytics(), loadMetrics()]);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Load data when filters change
  useEffect(() => {
    if (visitorId) {
      loadData();
    }
  }, [visitorId, filters, dateRange]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Process metrics for charts
  const chartData = useMemo(() => {
    if (!metrics.length) return [];

    // Group metrics by date
    const groupedByDate = metrics.reduce((acc, metric) => {
      const date = format(parseISO(metric.checkedAt), 'MMM dd');
      
      if (!acc[date]) {
        acc[date] = {
          date,
          totalChecks: 0,
          successfulChecks: 0,
          priceChanges: 0,
          newSales: 0,
          salesEnded: 0,
          notifications: 0,
          avgDuration: 0,
          durations: []
        };
      }

      acc[date].totalChecks++;
      if (metric.checkStatus.success) acc[date].successfulChecks++;
      if (metric.changes.anyPriceChanged) acc[date].priceChanges++;
      if (metric.changes.newSaleDetected) acc[date].newSales++;
      if (metric.changes.saleEnded) acc[date].salesEnded++;
      if (metric.notifications.sent) acc[date].notifications++;
      
      if (metric.checkStatus.checkDurationMs) {
        acc[date].durations.push(metric.checkStatus.checkDurationMs);
      }

      return acc;
    }, {});

    // Calculate averages and return sorted data
    return Object.values(groupedByDate)
      .map(day => ({
        ...day,
        avgDuration: day.durations.length > 0 
          ? Math.round(day.durations.reduce((a, b) => a + b, 0) / day.durations.length)
          : 0,
        successRate: day.totalChecks > 0 ? Math.round((day.successfulChecks / day.totalChecks) * 100) : 0
      }))
      .sort((a, b) => new Date(a.date + ' 2024') - new Date(b.date + ' 2024'));
  }, [metrics]);

  // Parser performance data
  const parserData = useMemo(() => {
    if (!metrics.length) return [];

    const parserStats = metrics.reduce((acc, metric) => {
      const parser = metric.checkStatus.parserUsed || 'unknown';
      
      if (!acc[parser]) {
        acc[parser] = {
          name: parser,
          totalChecks: 0,
          successfulChecks: 0,
          totalDuration: 0,
          durations: []
        };
      }

      acc[parser].totalChecks++;
      if (metric.checkStatus.success) acc[parser].successfulChecks++;
      if (metric.checkStatus.checkDurationMs) {
        acc[parser].totalDuration += metric.checkStatus.checkDurationMs;
        acc[parser].durations.push(metric.checkStatus.checkDurationMs);
      }

      return acc;
    }, {});

    return Object.values(parserStats).map(parser => ({
      ...parser,
      successRate: parser.totalChecks > 0 
        ? Math.round((parser.successfulChecks / parser.totalChecks) * 100)
        : 0,
      avgDuration: parser.durations.length > 0
        ? Math.round(parser.totalDuration / parser.durations.length)
        : 0
    }));
  }, [metrics]);

  // Stats cards data
  const statsCards = useMemo(() => {
    if (!analytics) return [];

    return [
      {
        title: 'Total Checks',
        value: analytics.totalChecks,
        icon: FiActivity,
        color: 'blue',
        change: null
      },
      {
        title: 'Success Rate',
        value: `${analytics.successRate}%`,
        icon: FiCheck,
        color: 'green',
        change: null
      },
      {
        title: 'Price Changes',
        value: analytics.priceChanges,
        icon: FiTrendingUp,
        color: 'yellow',
        change: null
      },
      {
        title: 'New Sales',
        value: analytics.newSalesDetected,
        icon: FiTrendingDown,
        color: 'purple',
        change: null
      },
      {
        title: 'Notifications Sent',
        value: analytics.notificationsSent,
        icon: FiAlertCircle,
        color: 'indigo',
        change: null
      },
      {
        title: 'Avg Check Time',
        value: `${analytics.averageCheckDuration}ms`,
        icon: FiActivity,
        color: 'gray',
        change: null
      }
    ];
  }, [analytics]);

  // Show message if metrics are not available
  if (error.includes('503') || (!loading && !analytics && !error)) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <FiAlertCircle className="h-8 w-8 text-yellow-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-yellow-800 mb-2">
            Metrics Dashboard Unavailable
          </h3>
          <p className="text-sm text-yellow-700 mb-4">
            The metrics dashboard is only available in production environment. 
            Metrics tracking is disabled in local development to keep your database clean.
          </p>
          <div className="text-xs text-yellow-600 bg-yellow-100 rounded-md p-2 max-w-md mx-auto">
            Current environment: {process.env.NODE_ENV || 'development'}
          </div>
        </div>
      </div>
    );
  }

  if (loading && !refreshing) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-center h-48">
            <FiRefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
            <span className="ml-2 text-gray-600">Loading metrics...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Metrics Dashboard
            </h2>
            <p className="text-sm text-gray-600">
              Monitor your product checking performance and price changes
            </p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 sm:mt-0">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                showFilters ? 'bg-gray-50' : ''
              }`}
            >
              <FiFilter className="h-4 w-4 mr-2" />
              Filters
            </button>
            
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <FiRefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date Range
                </label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => handleFilterChange('dateRange', parseInt(e.target.value))}
                  className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                >
                  <option value={1}>Last 24 hours</option>
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product
                </label>
                <select
                  value={filters.productId}
                  onChange={(e) => handleFilterChange('productId', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                >
                  <option value="">All Products</option>
                  {monitoredProducts.map(product => (
                    <option key={product._id} value={product._id}>
                      {product.title.length > 30 
                        ? product.title.substring(0, 30) + '...' 
                        : product.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Show
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.changesOnly}
                      onChange={(e) => handleFilterChange('changesOnly', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Changes only</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.successOnly}
                      onChange={(e) => handleFilterChange('successOnly', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Success only</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom Date Range
                </label>
                <div className="space-y-2">
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  />
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <FiX className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          {statsCards.map((stat, index) => (
            <div key={index} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <stat.icon className={`h-6 w-6 text-${stat.color}-600`} />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.title}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stat.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Price Changes Over Time */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Price Changes Over Time
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="priceChanges" 
                  stroke={COLORS.warning} 
                  name="Price Changes"
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="newSales" 
                  stroke={COLORS.success} 
                  name="New Sales"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Success Rate */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Success Rate & Performance
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="successRate" 
                  stroke={COLORS.success} 
                  fill={COLORS.success}
                  fillOpacity={0.3}
                  name="Success Rate (%)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Parser Performance */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Parser Performance
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={parserData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="avgDuration" fill={COLORS.primary} name="Avg Duration (ms)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Check Distribution */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Daily Check Distribution
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="totalChecks" fill={COLORS.primary} name="Total Checks" />
                <Bar dataKey="successfulChecks" fill={COLORS.success} name="Successful" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Metrics Table */}
      {metrics.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Recent Metrics ({metrics.length} records)
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Changes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parser
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {metrics.slice(0, 50).map((metric) => {
                  const product = monitoredProducts.find(p => p._id === metric.productId);
                  return (
                    <tr key={metric._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(parseISO(metric.checkedAt), 'MMM dd, HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {product ? (
                          <div className="flex items-center">
                            <img 
                              src={product.image} 
                              alt="" 
                              className="h-8 w-8 rounded object-cover mr-2"
                            />
                            <span className="truncate max-w-xs">
                              {product.title}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-500">Unknown Product</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          metric.checkStatus.success 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {metric.checkStatus.success ? 'Success' : 'Failed'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex space-x-1">
                          {metric.changes.newSaleDetected && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              New Sale
                            </span>
                          )}
                          {metric.changes.salePriceChanged && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Price Change
                            </span>
                          )}
                          {metric.changes.saleEnded && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Sale Ended
                            </span>
                          )}
                          {!metric.changes.anyPriceChanged && (
                            <span className="text-gray-400 text-xs">No changes</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {metric.checkStatus.checkDurationMs 
                          ? `${metric.checkStatus.checkDurationMs}ms`
                          : '-'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="capitalize">
                          {metric.checkStatus.parserUsed || 'unknown'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No data message */}
      {!loading && metrics.length === 0 && !error && (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <FiActivity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No metrics data found
          </h3>
          <p className="text-sm text-gray-500">
            Try adjusting your filters or check back after some product checks have been performed.
          </p>
        </div>
      )}
    </div>
  );
}

export default MetricsDashboard;
